/**
 * "Attach file" — the one control that puts a copy of a document beside a
 * transaction (owner decision 8, 2026-11-02; owner GRANT G15).
 *
 * WHY THIS IS ITS OWN MODULE. The owner's words: "Just a simple upload of an
 * image or file that attaches to the transaction ... as long as it is pretty
 * simple to add." Lane A leaves an empty `data-slot="attachments"` in the
 * transaction detail; the control is built here once so that slot (and any
 * later surface) gets the same rules, the same sentences and the same signed
 * URL handling instead of a second copy.
 *
 * WHAT IT DOES, AND WHAT IT REFUSES TO DO.
 *   - One button: pick a file, it uploads, the list grows. Nothing is read,
 *     previewed, OCR'd or categorised, and nothing here can change a booking —
 *     the Worker stores the file and serves it back, nothing more.
 *   - LAW 1, said in one plain sentence under the control: attach the receipt
 *     or bill only, nothing about a person's health or identity. Books does
 *     not scan for it; the person is told instead. (The sentence cannot use the
 *     Zone-1 banned person-role noun itself — the PHI-fieldname lint refuses it
 *     in code, the same reason `plaid-client.ts` names its dropped field by
 *     omission — so it says the same instruction in ordinary words.)
 *   - The file name is untrusted text. The Worker stores a sanitised copy and
 *     this module STILL renders it through the shared strip, because the name
 *     crosses the wire twice.
 *   - Who added it: the Worker's `uploadedBy` is `staff_roles.identity_key` —
 *     an email address, or an opaque sign-in subject. `face.person` decides
 *     whether that is a name a reader knows; an unknown identity is kept behind
 *     "Show details" and never printed as a face.
 *   - A file opens through a short-lived signed URL minted on demand, and only
 *     over https. It opens in ONE press: the tab is opened inside the press's
 *     own gesture (before the mint's await), because a window opened after an
 *     `await` is a popup the browser blocks. A blocked tab is said in words.
 *   - Withdrawing a file is offered to the person who uploaded it and to the
 *     owner for any file (owner amendment 2026-11-02: "An AM could accidentally
 *     upload a photo so they should be able to remove one they add - the owner
 *     can remove their own upload or an am upload"). It is a WITHDRAW, never a
 *     delete: the file stops showing, and the record of who uploaded it and who
 *     withdrew it is kept. The confirm is never pre-selected.
 *
 * The host supplies the reads and writes, so this module holds no fetch, no
 * route and no company logic.
 */
import { h } from "../dom.js";
import type {
  CompanySlug,
  StaffRole,
  TransactionAttachment,
  TransactionAttachmentListResponse,
  TransactionAttachmentSource,
  TransactionAttachmentUploadResponse,
  TransactionAttachmentUrlResponse,
} from "../types.js";
import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import { advice, mayWithContext } from "./capabilities.js";
import { timeAgoText } from "./display-words.js";
import { failureCopy, outcomeSentence } from "./failure.js";
import { person } from "./face.js";
import { runWrite } from "./write.js";

/**
 * What a person may pick. The Worker checks the bytes; this is the picker's
 * hint. HEIC/HEIF are the iPhone's own photo format (owner amendment
 * 2026-11-02: "Yes to both iPhone …"), so a photo attaches without being
 * converted first.
 */
export const ATTACHMENT_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/heif",
] as const;

/** The suffixes the picker also accepts when a file declares no type. */
export const ATTACHMENT_EXTENSIONS = [
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
  ".heic",
  ".heif",
] as const;

export const ATTACHMENT_ACCEPT = [
  ...ATTACHMENT_MIME_TYPES,
  ...ATTACHMENT_EXTENSIONS,
].join(",");

/** The same 10 MiB bound the Worker enforces (the readable half of the rule). */
export const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

/**
 * Law 1, as one plain instruction: the receipt or bill, and nothing about a
 * person's health or identity. Said once, under the control, before anything
 * is picked. Books does not scan the file; the person is told.
 *
 * (Two clauses rather than the brief's one because the Zone-1 banned
 * person-role noun cannot appear in code — `scripts/phi_fieldname_lint.py`
 * fails the build on it. The instruction is the same.)
 */
export const ATTACH_PHI_SENTENCE =
  "Attach the receipt or bill only. Nothing about a person's health or identity.";

/** What the file is for, in one plain sentence. No slug, no code. */
export const ATTACH_WHAT_TEXT =
  "A copy of the receipt or bill this line came from, kept as a backup. Books does not read it or use it in the books.";

/** The empty state. "None yet", never "0 files". */
export const ATTACH_EMPTY_TEXT = "No files attached to this transaction yet.";

/** A landed upload. */
export const ATTACH_DONE_TEXT = "Attached.";

/** A landed upload whose list did not repaint — the upload is not in doubt. */
export const ATTACH_DONE_NOT_REFRESHED_TEXT =
  "Attached. The list did not refresh; close this and open the transaction again to see it.";

/** A file the browser refused before anything was sent. */
export const ATTACH_WRONG_TYPE_TEXT =
  "Attach a PDF, a picture or a scan: PDF, JPEG, PNG, HEIC or HEIF.";
export const ATTACH_TOO_LARGE_TEXT = "That file is bigger than 10 MB. Attach a smaller file.";
export const ATTACH_EMPTY_FILE_TEXT = "That file is empty. Choose a different file.";

/**
 * The withdraw confirm (owner amendment 2026-11-02). Plain words: what it does,
 * what stays. "Withdraw" is the owner's own word for it, and the record is the
 * point — nothing is deleted.
 */
export const ATTACH_WITHDRAW_CONFIRM_TEXT =
  "Withdraw this file? It will no longer show on this transaction. A record that it was attached and withdrawn is kept.";

/** The word on the control that opens the confirm. */
export const ATTACH_WITHDRAW_CONTROL_TEXT = "Withdraw";

/** The word on the control that confirms it. */
export const ATTACH_WITHDRAW_CONFIRM_CONTROL_TEXT = "Withdraw file";

/** The word on the control that closes the confirm without withdrawing. */
export const ATTACH_WITHDRAW_CANCEL_TEXT = "Keep it";

/** A withdrawn file is gone from this list. */
export const ATTACH_WITHDRAWN_TEXT = "Withdrawn.";

/** The withdraw landed but the list did not repaint. */
export const ATTACH_WITHDRAWN_NOT_REFRESHED_TEXT =
  "Withdrawn. The list did not refresh; close this and open the transaction again to see it.";

/** The minted URL was not https, so it is never used. */
export const ATTACH_NO_OPEN_TEXT = "That file could not be opened.";

/**
 * The browser refused the tab this press opened (rd16 U, C13). A real state
 * with a real next step, never silently swallowed.
 */
export const ATTACH_OPEN_BLOCKED_TEXT =
  "Your browser blocked the new tab. Allow pop-ups for this page, then press Open again.";

/** The list read failed and there is nothing to show. */
export const ATTACH_READ_FAILED_TEXT = "The files for this transaction could not be read.";

/** The client calls this module makes. A host cannot wire a bare one around it. */
export interface AttachmentsClient {
  listTransactionAttachments(
    company: CompanySlug,
    source: TransactionAttachmentSource,
    refId: string,
  ): Promise<TransactionAttachmentListResponse>;
  uploadTransactionAttachment(
    company: CompanySlug,
    source: TransactionAttachmentSource,
    refId: string,
    file: File,
  ): Promise<TransactionAttachmentUploadResponse>;
  mintTransactionAttachmentUrl(
    company: CompanySlug,
    attachmentId: string,
  ): Promise<TransactionAttachmentUrlResponse>;
  withdrawTransactionAttachment(
    company: CompanySlug,
    attachmentId: string,
  ): Promise<TransactionAttachmentListResponse>;
}

export interface AttachmentsHost {
  role: StaffRole;
  company: CompanySlug;
  /** Which console line: a bank line, or a wallet payment. */
  source: TransactionAttachmentSource;
  /** `ActivityItem.refId` — the line's stable reference. */
  refId: string;
  /**
   * The signed-in person's `staff_roles.identity_key` (GET /v1/admin/me). The
   * console needs it to know which files this reader may withdraw; the Worker
   * is the enforcement, never this field.
   */
  viewerIdentity: string;
  client: AttachmentsClient;
  /**
   * Where a host wants the same sentence announced a second time. Optional: the
   * control's own status line is already `role="status"`, so a host that also
   * draws a live region of its own showed every sentence twice (rd16 U, C11).
   */
  announce?: (message: string) => void;
  /** Re-read after an upload (the host's own read), when it has one. */
  reload?: () => void | Promise<void>;
}

export interface AttachmentsHandle {
  root: HTMLElement;
  /** Re-read this transaction's files (the host calls it after its own write). */
  reload: () => Promise<void>;
}

/**
 * How long before a minted URL's expiry the console stops reusing it (rd16 U
 * review, item 6). A minute covers the time between the check and the browser's
 * request without throwing away a URL the person can still use.
 */
export const SIGNED_URL_REUSE_MARGIN_MS = 60 * 1000;

/** A signed URL is a bearer credential, so it is only ever used over https. */
function safeSignedUrl(url: string): string | null {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" ? parsed.href : null;
  } catch {
    return null;
  }
}

/** The visible separator between two facts on a file's line (rd16 U, C12). */
function separator(): Node {
  return document.createTextNode(" · ");
}

/**
 * The word for a file's kind on its row. Never a MIME type.
 */
function kindWord(attachment: TransactionAttachment): string {
  if (attachment.contentType === "application/pdf") return "PDF";
  return "Picture";
}

/** `340 KB` / `1.4 MB`, for "Show details" only. */
export function attachmentSizeText(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024) return `${String(bytes)} bytes`;
  if (bytes < 1024 * 1024) {
    return `${String(Math.max(1, Math.round(bytes / 1024)))} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** What the browser can refuse without sending anything (the Worker refuses too). */
export function attachmentLocalRefusal(file: File): string | null {
  const type = file.type.toLowerCase();
  if (
    type.length > 0 &&
    !(ATTACHMENT_MIME_TYPES as readonly string[]).includes(type)
  ) {
    return ATTACH_WRONG_TYPE_TEXT;
  }
  if (type.length === 0) {
    const name = file.name.toLowerCase();
    if (!ATTACHMENT_EXTENSIONS.some((suffix) => name.endsWith(suffix))) {
      return ATTACH_WRONG_TYPE_TEXT;
    }
  }
  if (file.size <= 0) return ATTACH_EMPTY_FILE_TEXT;
  if (file.size > ATTACHMENT_MAX_BYTES) return ATTACH_TOO_LARGE_TEXT;
  return null;
}

/**
 * The words a host renders when this reader may not attach a file at all — the
 * capability table's own sentence, so a host cannot invent a second wording.
 */
export function attachFileAdvice(role: StaffRole): string {
  return advice("attach_file", role);
}

export function mountAttachments(host: AttachmentsHost): AttachmentsHandle {
  const { role, company, source, refId, viewerIdentity } = host;
  const mayAttach = mayWithContext("attach_file", role);

  let rows: readonly TransactionAttachment[] = [];
  let loading = false;
  let failed: unknown = null;
  /**
   * The signed, https-usable URL per attachment id, once minted, and the instant
   * it stops being safe to reuse (rd16 U review, item 6).
   *
   * A signed URL lives about fifteen minutes. The first cut cached it with no
   * expiry, so pressing Open again after the window opened the dead link with
   * no new mint. A cached URL is now reused only while it has at least
   * {@link SIGNED_URL_REUSE_MARGIN_MS} left.
   */
  const openUrls = new Map<string, { href: string; usableUntilMs: number }>();

  const root = h("div", {
    class: "attachments",
    "data-role": "attachments",
    "data-source": source,
  });
  const status = h("p", {
    class: "status",
    role: "status",
    "data-role": "attachments-status",
  });
  const listHost = h("div", { "data-role": "attachments-list" });
  const controlHost = h("div", { "data-role": "attachments-control" });

  const say = (words: string): void => {
    status.textContent = words;
    host.announce?.(words);
  };

  const paintList = (): void => {
    while (listHost.firstChild) listHost.removeChild(listHost.firstChild);
    if (loading && rows.length === 0) {
      listHost.append(
        h("p", { class: "muted", "data-role": "attachments-loading" }, [
          "Reading the files for this transaction…",
        ]),
      );
      return;
    }
    if (failed !== null && rows.length === 0) {
      const copy = failureCopy(failed, { role, context: "read" });
      const retry = h(
        "button",
        {
          type: "button",
          class: "btn btn-secondary",
          "data-action": "retry",
          "aria-label": "Try again",
        },
        ["Try again"],
      );
      retry.addEventListener("click", () => {
        void reloadAll();
      });
      listHost.append(
        h("p", { class: "muted", "data-role": "attachments-unreadable" }, [
          ATTACH_READ_FAILED_TEXT,
        ]),
        h("p", { class: "muted" }, [copy.sentence]),
        retry,
      );
      return;
    }
    if (rows.length === 0) {
      listHost.append(
        h("p", { class: "muted", "data-role": "attachments-empty" }, [
          ATTACH_EMPTY_TEXT,
        ]),
      );
      return;
    }
    const list = h("ul", { class: "attachments-list", "data-role": "attachment-rows" });
    for (const attachment of rows) list.append(rowOf(attachment));
    listHost.append(list);
  };

  /** One file: its name, what it is, who added it and when. */
  const rowOf = (attachment: TransactionAttachment): HTMLElement => {
    const item = h("li", {
      class: "attachment-row",
      "data-role": "attachment-row",
      "data-attachment-id": attachment.id,
    });
    // rd16 U, C12: the parts ran together ("receipt.pdfPDFAdded by…") because
    // they were adjacent spans with no words between them. One line, one
    // separator: name · type · added by · when.
    const line = h("span", { class: "attachment-line", "data-role": "attachment-line" });
    line.append(
      h("span", { class: "attachment-name", "data-role": "attachment-name" }, [
        // Untrusted upload text: the shared strip, even though the Worker
        // already sanitised it for storage.
        amVisibleDisplayText(attachment.filename),
      ]),
      separator(),
      h("span", { class: "attachment-kind muted", "data-role": "attachment-kind" }, [
        kindWord(attachment),
      ]),
    );
    const who = person(attachment.uploadedBy);
    const added: string[] = [];
    if (who !== null) {
      added.push(`Added by ${amVisibleDisplayText(who)}`);
    } else {
      // A sign-in id is not a name a reader knows (face.person). Say when, and
      // leave the identity to "Show details" rather than print a subject.
      added.push("Added");
    }
    const when = timeAgoText(attachment.uploadedAt);
    if (when !== "") added.push(when);
    line.append(separator(), h("span", { class: "attachment-added muted", "data-role": "attachment-added" }, [added.join(" ")]));
    item.append(line);
    item.append(openControl(attachment));
    item.append(withdrawControl(attachment));
    item.append(
      h("details", { class: "disclosure", "data-role": "disclosure" }, [
        h("summary", {}, ["Show details"]),
        h("div", { class: "disclosure-body" }, [
          h("p", {}, [`Size: ${attachmentSizeText(attachment.byteSize)}`]),
          h("p", {}, [`Added by: ${amVisibleDisplayText(attachment.uploadedBy)}`]),
          h("p", {}, [`File id: ${amVisibleDisplayText(attachment.id)}`]),
        ]),
      ]),
    );
    return item;
  };

  /**
   * The withdraw control, offered only where the Worker would allow it: the
   * person who uploaded this file, or the owner for any file. `capabilities.ts`
   * owns the rule and fails closed when the caller does not say whose upload it
   * is; `ownUpload` is that answer for this row.
   */
  const withdrawControl = (attachment: TransactionAttachment): HTMLElement => {
    const holder = h("span", { class: "attachment-withdraw" });
    const ownUpload = attachment.uploadedBy === viewerIdentity;
    if (!mayWithContext("withdraw_attachment", role, { ownUpload })) {
      return holder;
    }
    const open = h(
      "button",
      {
        type: "button",
        class: "btn btn-quiet",
        "data-action": "withdraw-attachment",
        "aria-label": `Withdraw ${amVisibleDisplayText(attachment.filename)}`,
      },
      [ATTACH_WITHDRAW_CONTROL_TEXT],
    );
    let confirmOpen = false;
    let inFlight = false;
    open.addEventListener("click", () => {
      if (confirmOpen || inFlight) return;
      confirmOpen = true;
      const confirmHost = h("div", {
        class: "confirm-line",
        "data-role": "attachment-withdraw-confirm",
      });
      const yes = h(
        "button",
        {
          type: "button",
          class: "btn btn-primary",
          "data-action": "confirm-withdraw-attachment",
        },
        [ATTACH_WITHDRAW_CONFIRM_CONTROL_TEXT],
      );
      const keep = h(
        "button",
        {
          type: "button",
          class: "btn btn-ghost",
          "data-action": "cancel-withdraw-attachment",
        },
        [ATTACH_WITHDRAW_CANCEL_TEXT],
      );
      // Deliberately no focus() and no pre-selected state: the person must
      // choose, not accept a default destructive action.
      confirmHost.append(
        h("p", { "data-role": "attachment-withdraw-copy" }, [
          ATTACH_WITHDRAW_CONFIRM_TEXT,
        ]),
        h("div", { class: "form-row" }, [yes, keep]),
      );
      keep.addEventListener("click", () => {
        confirmOpen = false;
        confirmHost.remove();
      });
      yes.addEventListener("click", () => {
        void (async () => {
          if (inFlight) return;
          inFlight = true;
          yes.disabled = true;
          keep.disabled = true;
          try {
            const outcome = await runWrite({
              key: `withdraw-attachment:${attachment.id}`,
              scope: root,
              gate: () => null,
              send: () =>
                host.client.withdrawTransactionAttachment(
                  company,
                  attachment.id,
                ),
              stillCurrent: () => root.isConnected,
              afterLanded: (body) => {
                rows = body.attachments;
                openUrls.clear();
                paintList();
              },
            });
            if (outcome.kind === "landed") {
              say(
                outcome.afterLandedFailed === true
                  ? ATTACH_WITHDRAWN_NOT_REFRESHED_TEXT
                  : ATTACH_WITHDRAWN_TEXT,
              );
              if (host.reload !== undefined && outcome.afterLandedFailed === true) {
                void host.reload();
              }
            } else {
              say(outcomeSentence(outcome, role));
            }
          } finally {
            inFlight = false;
            confirmOpen = false;
            confirmHost.remove();
          }
        })();
      });
      holder.append(confirmHost);
    });
    holder.append(open);
    return holder;
  };

  /**
   * "Open" mints the signed URL and opens the file, in ONE press (rd16 U, C13).
   *
   * THE DEFECT. The control minted on the first press and left a second link to
   * press. A person reported the two-step as a bug, and they were right: one
   * press is what "Open" promises.
   *
   * THE POPUP BLOCKER, AND WHY THE WINDOW IS OPENED FIRST. A browser allows a
   * new tab only inside the event handler that the press started; the `await`
   * that mints the URL ends that gesture, so `window.open` after it is blocked.
   * The tab is therefore opened EMPTY synchronously, and its location is set
   * once the minted URL arrives. A blocked tab (`null`) is a real state that is
   * said in words, not ignored.
   */
  const openControl = (attachment: TransactionAttachment): HTMLElement => {
    const host_ = h("span", { class: "attachment-open" });
    const open = h(
      "button",
      {
        type: "button",
        class: "btn btn-quiet",
        "data-action": "open-attachment",
        "aria-label": `Open ${amVisibleDisplayText(attachment.filename)}`,
      },
      ["Open"],
    );
    open.addEventListener("click", () => {
      if (open.disabled) return;
      open.disabled = true;
      // Same gesture: the tab exists before any await.
      const target = window.open("", "_blank");
      if (target !== null) {
        // A signed URL is a bearer credential, so the tab must not keep a
        // handle back to the console. Some environments make it read-only.
        try {
          target.opener = null;
        } catch {
          // Keep the tab; the URL is still short-lived and same-origin-free.
        }
      }
      void mintAndOpen(attachment, open, target);
    });
    host_.append(open);
    return host_;
  };

  /**
   * Mint the short-lived URL for one file and send the tab opened by the press
   * to it (rd16 U, C13).
   *
   * A URL already minted for this file is reused ONLY while it has at least
   * {@link SIGNED_URL_REUSE_MARGIN_MS} left before the Worker's own expiry
   * (rd16 U review, item 6): a cached URL with no expiry opened a dead link a
   * quarter of an hour after the first press. A mint whose `expiresAt` cannot
   * be read is not cached at all, so the next press mints again rather than
   * opening something it cannot vouch for.
   */
  const mintAndOpen = async (
    attachment: TransactionAttachment,
    button: HTMLButtonElement,
    target: Window | null,
  ): Promise<void> => {
    try {
      const cached = openUrls.get(attachment.id);
      const reusable =
        cached !== undefined && Date.now() < cached.usableUntilMs ? cached : null;
      let href: string | null;
      if (reusable !== null) {
        href = reusable.href;
      } else {
        const minted = await host.client.mintTransactionAttachmentUrl(
          company,
          attachment.id,
        );
        href = safeSignedUrl(minted.url);
        if (href !== null) {
          const expiresMs = Date.parse(minted.expiresAt);
          if (Number.isFinite(expiresMs)) {
            openUrls.set(attachment.id, {
              href,
              usableUntilMs: expiresMs - SIGNED_URL_REUSE_MARGIN_MS,
            });
          } else {
            openUrls.delete(attachment.id);
          }
        }
      }
      if (href === null) {
        target?.close();
        say(ATTACH_NO_OPEN_TEXT);
        return;
      }
      if (target === null) {
        say(ATTACH_OPEN_BLOCKED_TEXT);
        return;
      }
      target.location.href = href;
    } catch (error) {
      // Generic wording only; the server's own words go behind "Show details"
      // in the read context of the one failure module.
      target?.close();
      const copy = failureCopy(error, { role, context: "read" });
      say(copy.sentence);
    } finally {
      button.disabled = false;
    }
  };

  const paintControl = (): void => {
    while (controlHost.firstChild) controlHost.removeChild(controlHost.firstChild);
    if (!mayAttach) {
      // The capability table owns the sentence; the control is not offered.
      controlHost.append(
        h("p", { class: "muted", "data-role": "attachments-advice" }, [
          attachFileAdvice(role),
        ]),
      );
      return;
    }
    // rd16 U, C14: one action, one control. The browser's own "Choose File"
    // input sat beside this button, so picking a file there attached it at once
    // and the button's own job was unclear. The input stays in the DOM (the
    // browser needs a real file input) but is visually hidden and taken out of
    // the tab order, so the button is the one thing to press and it opens the
    // picker.
    const input = h("input", {
      type: "file",
      class: "attachments-input",
      accept: ATTACHMENT_ACCEPT,
      "aria-label": "Choose a file to attach",
      "data-role": "attachments-input",
      tabindex: "-1",
      "aria-hidden": "true",
    });
    const attach = h(
      "button",
      {
        type: "button",
        class: "btn btn-primary",
        "data-action": "attach-file",
        "aria-label": "Attach file",
      },
      ["Attach file"],
    );
    attach.addEventListener("click", () => {
      input.click();
    });
    input.addEventListener("change", () => {
      const file = input.files?.[0];
      if (file === undefined) return;
      void sendFile(file, input);
    });
    controlHost.append(attach, input);
  };

  const sendFile = async (
    file: File,
    input: HTMLInputElement,
  ): Promise<void> => {
    const refusal = attachmentLocalRefusal(file);
    if (refusal !== null) {
      say(refusal);
      input.value = "";
      return;
    }
    // One write primitive: one flight per line, the whole control disabled, and
    // one failure wording for a refusal, a conflict or a lost answer.
    const outcome = await runWrite({
      key: `attach-file:${source}:${refId}`,
      scope: root,
      gate: () => null,
      send: () => host.client.uploadTransactionAttachment(company, source, refId, file),
      stillCurrent: () => root.isConnected,
      afterLanded: async () => {
        await reloadAll();
      },
    });
    if (outcome.kind === "landed") {
      say(
        outcome.afterLandedFailed === true
          ? ATTACH_DONE_NOT_REFRESHED_TEXT
          : ATTACH_DONE_TEXT,
      );
      if (host.reload !== undefined && outcome.afterLandedFailed === true) {
        void host.reload();
      }
    } else {
      say(outcomeSentence(outcome, role));
    }
    input.value = "";
  };

  const reloadAll = async (): Promise<void> => {
    loading = true;
    failed = null;
    paintList();
    try {
      const body = await host.client.listTransactionAttachments(
        company,
        source,
        refId,
      );
      rows = body.attachments;
      // A fresh list means any URL minted for an older row may no longer match
      // what is shown; drop them so a person never opens a stale link.
      openUrls.clear();
    } catch (error) {
      failed = error;
      if (rows.length === 0) rows = [];
    } finally {
      loading = false;
      paintList();
    }
  };

  root.append(
    h("p", { class: "field-label" }, ["Files"]),
    h("p", { class: "muted", "data-role": "attachments-what" }, [ATTACH_WHAT_TEXT]),
    h("p", { class: "muted", "data-role": "attachments-phi" }, [ATTACH_PHI_SENTENCE]),
    controlHost,
    status,
    listHost,
  );
  paintControl();
  paintList();
  void reloadAll();

  return { root, reload: reloadAll };
}
